import React, { useState } from 'react';
import { useUserConversation } from '../context/UserConversationContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
// import { UserConversation } from '../types';

const ChatHistoryPage: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const {
    conversations,
    currentConversation,
    messages,
    loading,
    selectConversation,
    clearCurrentConversation,
    loadConversations
  } = useUserConversation();

  const handleSelectConversation = (conversationId: string) => {
    selectConversation(conversationId);
  };

  const handleNewChat = () => {
    clearCurrentConversation();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadConversations({ search: searchQuery });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff'
    }}>
      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? '320px' : '0',
        backgroundColor: theme === 'dark' ? '#2d333a' : '#f7f7f8',
        borderRight: `1px solid ${theme === 'dark' ? '#333333' : '#f0f0f0'}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${theme === 'dark' ? '#333333' : '#f0f0f0'}`
        }}>
          <button
            onClick={handleNewChat}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              backgroundColor: '#10a37f',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              marginBottom: '1rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#0d8f6b';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#10a37f';
            }}
          >
            + New Chat
          </button>

          {/* Search */}
          <form onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                borderRadius: '0.5rem',
                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                fontSize: '0.875rem'
              }}
            />
          </form>
        </div>

        {/* Conversation History */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem'
          }}>
            <h3 style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: theme === 'dark' ? '#ffffff' : '#2d333a',
              margin: 0
            }}>
              Saved Conversations ({filteredConversations.length})
            </h3>
          </div>

          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: theme === 'dark' ? '#8e9297' : '#8e9297'
            }}>
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <p style={{
              textAlign: 'center',
              color: theme === 'dark' ? '#8e9297' : '#8e9297',
              padding: '1rem',
              fontSize: '0.875rem'
            }}>
              {searchQuery ? 'No conversations found' : 'No saved conversations yet'}
            </p>
          ) : (
            filteredConversations.map((conversation) => (
              <div
                key={conversation._id}
                onClick={() => handleSelectConversation(conversation._id)}
                style={{
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  marginBottom: '0.5rem',
                  backgroundColor: currentConversation?._id === conversation._id ? '#10a37f' : 'transparent',
                  color: currentConversation?._id === conversation._id ? 'white' : (theme === 'dark' ? '#ffffff' : '#2d333a'),
                  border: `1px solid ${theme === 'dark' ? '#333333' : '#f0f0f0'}`
                }}
                onMouseOver={(e) => {
                  if (currentConversation?._id !== conversation._id) {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#333333' : '#f0f0f0';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentConversation?._id !== conversation._id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {conversation.title}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: currentConversation?._id === conversation._id ? 'rgba(255, 255, 255, 0.8)' : (theme === 'dark' ? '#8e9297' : '#8e9297'),
                  marginBottom: '0.25rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {conversation.summary || 'No summary'}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: currentConversation?._id === conversation._id ? 'rgba(255, 255, 255, 0.7)' : (theme === 'dark' ? '#8e9297' : '#8e9297'),
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>{conversation.messageCount} messages</span>
                  <span>{new Date(conversation.updatedAt).toLocaleDateString()}</span>
                </div>
                {conversation.tags.length > 0 && (
                  <div style={{
                    marginTop: '0.5rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.25rem'
                  }}>
                    {conversation.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '0.625rem',
                          padding: '0.125rem 0.375rem',
                          backgroundColor: currentConversation?._id === conversation._id ? 'rgba(255, 255, 255, 0.2)' : (theme === 'dark' ? '#404040' : '#e5e5e5'),
                          color: currentConversation?._id === conversation._id ? 'white' : (theme === 'dark' ? '#ffffff' : '#2d333a'),
                          borderRadius: '0.25rem'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                    {conversation.tags.length > 3 && (
                      <span style={{
                        fontSize: '0.625rem',
                        color: currentConversation?._id === conversation._id ? 'rgba(255, 255, 255, 0.7)' : (theme === 'dark' ? '#8e9297' : '#8e9297')
                      }}>
                        +{conversation.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* User Profile */}
        <div style={{
          padding: '1.5rem',
          borderTop: `1px solid ${theme === 'dark' ? '#333333' : '#f0f0f0'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              flex: 1
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#10a37f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.125rem',
                fontWeight: 600
              }}>
                {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: theme === 'dark' ? '#8e9297' : '#8e9297',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {user?.email}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={toggleTheme}
                style={{
                  padding: '0.5rem',
                  background: 'none',
                  border: 'none',
                  color: theme === 'dark' ? '#8e9297' : '#8e9297',
                  cursor: 'pointer',
                  borderRadius: '0.25rem',
                  transition: 'all 0.15s ease'
                }}
                title="Toggle theme"
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
              <button
                onClick={logout}
                style={{
                  padding: '0.5rem',
                  background: 'none',
                  border: 'none',
                  color: theme === 'dark' ? '#8e9297' : '#8e9297',
                  cursor: 'pointer',
                  borderRadius: '0.25rem',
                  transition: 'all 0.15s ease'
                }}
                title="Logout"
              >
                🚪
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem 2rem',
          borderBottom: `1px solid ${theme === 'dark' ? '#333333' : '#f0f0f0'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                padding: '0.5rem',
                background: 'none',
                border: 'none',
                color: theme === 'dark' ? '#8e9297' : '#8e9297',
                cursor: 'pointer',
                borderRadius: '0.25rem',
                fontSize: '1.125rem'
              }}
            >
              ☰
            </button>
            <h1 style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: theme === 'dark' ? '#ffffff' : '#2d333a'
            }}>
              {currentConversation ? currentConversation.title : 'Conversation History'}
            </h1>
          </div>
        </div>

        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem'
        }}>
          {!currentConversation ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: '1.875rem',
                  fontWeight: 700,
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  marginBottom: '1rem'
                }}>
                  Your Conversation History
                </h2>
                <p style={{
                  fontSize: '1.125rem',
                  color: theme === 'dark' ? '#b4b4b4' : '#6e7681'
                }}>
                  Select a conversation from the sidebar to view its messages
                </p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center'
            }}>
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  marginBottom: '0.5rem'
                }}>
                  No messages found
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: theme === 'dark' ? '#b4b4b4' : '#6e7681'
                }}>
                  This conversation appears to be empty
                </p>
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={`${message.id}-${index}`}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  maxWidth: '100%',
                  flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  flexShrink: 0,
                  backgroundColor: message.role === 'user' ? '#10a37f' : (theme === 'dark' ? '#2d333a' : '#f7f7f8'),
                  color: message.role === 'user' ? 'white' : (theme === 'dark' ? '#ffffff' : '#2d333a')
                }}>
                  {message.role === 'user' ? '👤' : '🤖'}
                </div>
                <div style={{
                  flex: 1,
                  maxWidth: '70%'
                }}>
                  <div style={{
                    padding: '1rem 1.5rem',
                    borderRadius: '0.75rem',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    wordWrap: 'break-word',
                    backgroundColor: message.role === 'user' ? '#10a37f' : (theme === 'dark' ? '#2d333a' : '#f7f7f8'),
                    color: message.role === 'user' ? 'white' : (theme === 'dark' ? '#ffffff' : '#2d333a'),
                    border: message.role === 'assistant' ? `1px solid ${theme === 'dark' ? '#333333' : '#f0f0f0'}` : 'none',
                    borderBottomRightRadius: message.role === 'user' ? '0.25rem' : '0.75rem',
                    borderBottomLeftRadius: message.role === 'assistant' ? '0.25rem' : '0.75rem'
                  }}>
                    {message.content}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: theme === 'dark' ? '#8e9297' : '#8e9297',
                    marginTop: '0.25rem',
                    textAlign: message.role === 'user' ? 'right' : 'left'
                  }}>
                    {new Date(message.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryPage;
